import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { COUNTRIES } from "./countryMetadata";
import { resolveCountryPreference } from "./countryPreference";

const ALL_COUNTRY_CODES = COUNTRIES.map((country) => country.code);

export type UserCountry = {
  /** The profile country, or null while the profile is loading or has none. */
  countryCode: string | null;
  /** True until the profile has answered once this session. */
  isLoading: boolean;
};

/**
 * The signed-in patient's country: the one on her profile, and nothing else.
 * Every session gets one at sign-in (`CountryDefaultBoot`) and she changes it
 * under Personal info, so features read the profile and never guess. Null is
 * the honest answer for the moment the profile is still loading; a caller
 * shows nothing, or a neutral default, for that moment rather than another
 * country. Whether a feature gates on the country or only leans on it is
 * each feature's own call.
 */
export function useUserCountry({
  supportedCountryCodes = ALL_COUNTRY_CODES,
}: {
  /** Pass a module-level constant; the default is every known country. */
  supportedCountryCodes?: readonly string[];
} = {}): UserCountry {
  const { data: patient, isLoading } = usePatient();
  const resolved = resolveCountryPreference({
    savedCountry: patient?.addresses?.[0]?.country,
    supportedCountryCodes,
    fallbackCountry: "US",
  });
  return {
    countryCode: resolved.source === "saved" ? resolved.countryCode : null,
    isLoading,
  };
}
